import React, { useState, useEffect, useCallback } from 'react';
import {
  CardBody,
  Row,
  Col,
  Card,
  CardHeader,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalBody,
  Label,
  Input,
  Badge,
  Button,
  FormGroup,
  Spinner,
} from 'reactstrap';
import DataTable from 'react-data-table-component';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { api } from '../../../config';
import { getAuthToken } from '../../../auth/tokenvalidation';
import BreadCrumb from '../../../Components/Common/BreadCrumb';

const MenuManagement = () => {
  const token = getAuthToken();
  const [data, setData] = useState({ items: [] });
  const [parentOptions, setParentOptions] = useState([]);
  const [modal, setModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  // Table States
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [sortField, setSortField] = useState('orderNumber');
  const [sortDirection, setSortDirection] = useState('asc');

  const initialFormState = {
    id: null,
    code: '',
    label: '',
    icon: '',
    isActive: true,
    isHeader: false,
    link: '',
    orderNumber: 0,
    parentId: null,
  };

  const [formData, setFormData] = useState(initialFormState);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchText(searchText), 500);
    return () => clearTimeout(timer);
  }, [searchText]);

  const fetchParentMenus = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(
        `${api.API_URL}/menu?PageSize=1000&IsActive=true`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const result = await response.json();
      if (result.data?.items) {
        setParentOptions(
          result.data.items.map(item => ({
            label: item.label,
            value: item.id,
          }))
        );
      }
    } catch (error) {
      console.error('Failed fetching parent menus', error);
    }
  }, [token]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const queryParams = {
        PageNumber: currentPage.toString(),
        PageSize: rowsPerPage.toString(),
      };

      const qs = new URLSearchParams(queryParams);

      if (debouncedSearchText && debouncedSearchText.trim() !== '') {
        qs.append('Label', debouncedSearchText.trim());
      }

      if (sortField) {
        qs.append('SortBy', sortField);
        qs.append('IsDescending', sortDirection === 'desc' ? 'true' : 'false');
      }

      const response = await fetch(`${api.API_URL}/menu?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);

      const result = await response.json();
      setData({ items: result.data?.items || [] });
      setTotalRows(result.data?.totalItems || 0);
    } catch (error) {
      toast.error(`Error loading data: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  }, [
    currentPage,
    rowsPerPage,
    token,
    debouncedSearchText,
    sortField,
    sortDirection,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchParentMenus();
  }, [token]);

  const handleSearch = e => {
    setSearchText(e.target.value);
    setCurrentPage(1);
  };

  const toggleModal = () => {
    if (modal) {
      // setFormData(initialFormState);
      setErrors({});
    }
    setModal(!modal);
  };

  const handleEdit = row => {
    setFormData({
      id: row.id,
      code: row.code || '',
      label: row.label || '',
      icon: row.icon || '',
      isActive: row.isActive ?? true,
      isHeader: row.isHeader ?? false,
      link: row.link || '',
      orderNumber: row.orderNumber || 0,
      parentId: row.parentId || null,
    });
    setModal(true);
  };

  const handleDelete = async id => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
    });

    if (result.isConfirmed) {
      try {
        const response = await fetch(`${api.API_URL}/menu/${id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error('Delete failed');
        toast.success('Menu deleted successfully');
        fetchData();
      } catch (error) {
        toast.error(`Delete failed: ${error.message}`);
      }
    }
  };

  const validateForm = () => {
    const tempErrors = {};
    if (!formData.code) tempErrors.code = 'Code is required';
    if (!formData.label) tempErrors.label = 'Label is required';
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setModalLoading(true);

    try {
      const payload = { ...formData };
      const isEdit = !!formData.id;
      const url = isEdit
        ? `${api.API_URL}/menu/${formData.id}`
        : `${api.API_URL}/menu`;

      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error('Save operation failed');

      toast.success(`Menu ${isEdit ? 'updated' : 'created'} successfully`);
      toggleModal();
      fetchData();
      fetchParentMenus();
    } catch (error) {
      toast.error(`Save failed: ${error.message}`);
    } finally {
      setModalLoading(false);
    }
  };

  const columns = [
    {
      id: 'orderNumber',
      name: (
        <span className='text-uppercase fw-semibold fs-12 text-muted'>
          Order
        </span>
      ),
      selector: row => row.orderNumber,
      sortable: true,
      width: '80px',
      cell: row => <span className='fw-medium'>{row.orderNumber}</span>,
    },
    {
      id: 'code',
      name: (
        <span className='text-uppercase fw-semibold fs-12 text-muted'>
          Code
        </span>
      ),
      selector: row => row.code,
      sortable: true,
      minWidth: '150px',
      cell: row => <span className='text-dark fw-medium'>{row.code}</span>,
    },
    {
      id: 'label',
      name: (
        <span className='text-uppercase fw-semibold fs-12 text-muted'>
          Label
        </span>
      ),
      selector: row => row.label,
      sortable: true,
      minWidth: '200px',
      cell: row => (
        <div className='d-flex align-items-center gap-2'>
          {row.icon && <i className={`${row.icon} text-muted`}></i>}
          <span>{row.label}</span>
          {row.isHeader && (
            <Badge color='info' className='fs-10 rounded-pill ms-1'>
              Header
            </Badge>
          )}
        </div>
      ),
    },
    {
      name: (
        <span className='text-uppercase fw-semibold fs-12 text-muted'>
          Link
        </span>
      ),
      selector: row => row.link,
      minWidth: '200px',
      cell: row => <span className='text-muted'>{row.link || '-'}</span>,
    },
    {
      name: (
        <span className='text-uppercase fw-semibold fs-12 text-muted'>
          Status
        </span>
      ),
      selector: row => row.isActive,
      width: '100px',
      center: true,
      cell: row => (
        <Badge
          color={row.isActive ? 'success' : 'danger'}
          className='fs-11 rounded-pill'
        >
          {row.isActive ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      name: (
        <span className='text-uppercase fw-semibold fs-12 text-muted text-center w-100'>
          Action
        </span>
      ),
      width: '100px',
      center: true,
      cell: row => (
        <div className='d-flex gap-1 justify-content-center'>
          <button
            className='btn btn-soft-info btn-sm btn-icon'
            onClick={() => handleEdit(row)}
          >
            <i className='ri-pencil-fill align-bottom'></i>
          </button>
          <button
            className='btn btn-soft-danger btn-sm btn-icon'
            onClick={() => handleDelete(row.id)}
          >
            <i className='ri-delete-bin-6-line align-bottom'></i>
          </button>
        </div>
      ),
    },
  ];

  return (
    <React.Fragment>
      <BreadCrumb title='View Data' pageTitle='Menu Management' />
      <Row>
        <Col lg={12}>
          <Card className='border-0 shadow-sm'>
            <CardHeader className='border-0 pb-0'>
              <div className='d-flex align-items-center justify-content-between'>
                <h5 className='card-title mb-0'>Menu Management</h5>
                <Button
                  color='primary'
                  size='sm'
                  className='btn-label'
                  onClick={toggleModal}
                >
                  <i className='ri-add-line label-icon align-middle fs-16 me-2'></i>{' '}
                  Add Menu
                </Button>
              </div>
            </CardHeader>
            <CardBody>
              <Row className='mb-3'>
                <Col md={4}>
                  <div className='search-box'>
                    <Input
                      type='text'
                      className='form-control'
                      placeholder='Search menu label...'
                      value={searchText}
                      onChange={handleSearch}
                    />
                    <i className='ri-search-line search-icon'></i>
                  </div>
                </Col>
              </Row>
              <DataTable
                columns={columns}
                data={data.items}
                pagination
                paginationServer
                paginationTotalRows={totalRows}
                onChangePage={page => setCurrentPage(page)}
                onChangeRowsPerPage={rows => {
                  setRowsPerPage(rows);
                  setCurrentPage(1);
                }}
                onSort={(column, dir) => {
                  setSortField(column.id);
                  setSortDirection(dir);
                  setCurrentPage(1);
                }}
                sortServer
                highlightOnHover
                responsive
                striped
                progressPending={isLoading}
                progressComponent={<Spinner color='primary' className='my-4' />}
                noDataComponent={
                  <p className='my-4 text-muted'>No menu data available.</p>
                }
              />
              <ToastContainer closeButton={false} limit={1} />
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Modal
        isOpen={modal}
        toggle={toggleModal}
        size='lg'
        backdrop='static'
        centered
        onClosed={() => setFormData(initialFormState)}
      >
        <ModalHeader toggle={toggleModal} className='bg-light'>
          {formData.id ? 'Edit Menu' : 'Create New Menu'}
        </ModalHeader>
        <ModalBody>
          <Row className='g-3'>
            <Col md={6}>
              <div
                onClick={() =>
                  setFormData(prev => ({
                    ...prev,
                    isActive: !prev.isActive,
                  }))
                }
                className={`
        border rounded px-3 d-flex align-items-center justify-content-between
        transition-all cursor-pointer user-select-none
        ${formData.isActive ? 'border-success bg-success-subtle' : 'border-light bg-light'}
      `}
                style={{ height: '42px', transition: 'all 0.2s ease' }}
              >
                <Label className='mb-0 fw-medium fs-14 cursor-pointer'>
                  <i
                    className={`ri-shield-check-line me-2 ${formData.isActive ? 'text-success' : 'text-muted'}`}
                  ></i>
                  <span
                    className={
                      formData.isActive ? 'text-success' : 'text-muted'
                    }
                  >
                    Is Active
                  </span>
                </Label>
                <FormGroup
                  switch
                  className='mb-0'
                  onClick={e => e.stopPropagation()}
                >
                  <Input
                    type='switch'
                    role='switch'
                    checked={formData.isActive}
                    onChange={e =>
                      setFormData(prev => ({
                        ...prev,
                        isActive: e.target.checked,
                      }))
                    }
                  />
                </FormGroup>
              </div>
            </Col>

            <Col md={6}>
              <div
                onClick={() =>
                  setFormData(prev => {
                    const newIsHeader = !prev.isHeader;
                    return {
                      ...prev,
                      isHeader: newIsHeader,
                      parentId: newIsHeader ? null : prev.parentId,
                    };
                  })
                }
                className={`
        border rounded px-3 d-flex align-items-center justify-content-between
        transition-all cursor-pointer user-select-none
        ${formData.isHeader ? 'border-warning bg-warning-subtle' : 'border-light bg-light'}
      `}
                style={{ height: '42px', transition: 'all 0.2s ease' }}
              >
                <Label className='mb-0 fw-medium fs-14 cursor-pointer'>
                  <i
                    className={`ri-folder-line me-2 ${formData.isHeader ? 'text-warning' : 'text-muted'}`}
                  ></i>
                  <span
                    className={formData.isHeader ? 'text-dark' : 'text-muted'}
                  >
                    Is Header Label
                  </span>
                </Label>
                <FormGroup
                  switch
                  className='mb-0'
                  onClick={e => e.stopPropagation()}
                >
                  <Input
                    type='switch'
                    role='switch'
                    checked={formData.isHeader}
                    onChange={e =>
                      setFormData(prev => ({
                        ...prev,
                        isHeader: e.target.checked,
                        parentId: e.target.checked ? null : prev.parentId,
                      }))
                    }
                  />
                </FormGroup>
              </div>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>
                  Code <span className='text-danger'>*</span>
                </Label>
                <Input
                  type='text'
                  placeholder='e.g. MNU_USER'
                  value={formData.code}
                  invalid={!!errors.code}
                  onChange={e => {
                    setFormData(prev => ({ ...prev, code: e.target.value }));
                    if (errors.code)
                      setErrors(prev => ({ ...prev, code: undefined }));
                  }}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>
                  Label <span className='text-danger'>*</span>
                </Label>
                <Input
                  type='text'
                  placeholder='e.g. User Management'
                  value={formData.label}
                  invalid={!!errors.label}
                  onChange={e => {
                    setFormData(prev => ({ ...prev, label: e.target.value }));
                    if (errors.label)
                      setErrors(prev => ({ ...prev, label: undefined }));
                  }}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Link Path</Label>
                <Input
                  type='text'
                  placeholder='e.g. /apps-usermanagement'
                  value={formData.link || ''}
                  disabled={formData.isHeader}
                  onChange={e =>
                    setFormData(prev => ({ ...prev, link: e.target.value }))
                  }
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Icon Class</Label>
                <Input
                  type='text'
                  placeholder='e.g. ri-dashboard-line'
                  value={formData.icon || ''}
                  disabled={formData.isHeader}
                  onChange={e =>
                    setFormData(prev => ({ ...prev, icon: e.target.value }))
                  }
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Parent Menu</Label>
                <Select
                  options={modal ? parentOptions : []}
                  isClearable
                  placeholder='-- Select Parent --'
                  isDisabled={formData.isHeader}
                  value={
                    parentOptions.find(
                      opt => opt.value === formData.parentId
                    ) || null
                  }
                  onChange={opt =>
                    setFormData(prev => ({
                      ...prev,
                      parentId: opt?.value || null,
                    }))
                  }
                  styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Order Number</Label>
                <Input
                  type='number'
                  value={formData.orderNumber}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      orderNumber: parseInt(e.target.value) || 0,
                    }))
                  }
                />
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color='light' onClick={toggleModal} disabled={modalLoading}>
            Cancel
          </Button>
          <Button color='primary' onClick={handleSave} disabled={modalLoading}>
            {modalLoading ? (
              <Spinner size='sm' className='me-2' />
            ) : (
              <i className='ri-save-line me-1'></i>
            )}
            {formData.id ? 'Save Changes' : 'Create Menu'}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default MenuManagement;
